/**
 * Projection for `TechniciansScene` — the demo's `renderTechs()` (line 2719).
 *
 * Pure: no React, no fetching, no engine mutation. Every measured figure comes
 * from `techStats()` and `dispatchProposals()` in `shared/oem-engine.ts`; this
 * file only joins the roster, the directory and the LMS onto them, and writes
 * the label strings the demo writes.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/service/TechniciansScene.tsx
 * @see docs/logic/workflows/technicians.md
 * @see docs/foundations/4-layer-architecture.md
 */

import { MINES } from '../../data/core';
import type { MineCode } from '../../schema/machine';
import {
  dispatchProposals,
  meanUtil,
  techStats,
  type DispatchProposal,
  type TechStats,
} from '../../shared/oem-engine';
import type {
  CustomerSatisfactionResponse,
  PersonRecord,
  RosterEntry,
  TechnicianPresence,
  TrainingRecord,
} from '../../shared/useOemData';
import type {
  CssCtx,
  RosterCtx,
  TechJobCtx,
  TechPresenceCtx,
  TimesheetCtx,
  TrainingCtx,
  WorkOrderCtx,
} from '../../shared/oem-engine';

/** Everything this surface needs loaded. */
export type TechniciansCtx = TechPresenceCtx &
  TechJobCtx &
  CssCtx &
  WorkOrderCtx &
  TimesheetCtx &
  TrainingCtx &
  RosterCtx & {
    /** `useOemData('people').people` — the directory, for job titles. */
    readonly people: readonly PersonRecord[];
  };

/** One skill or certification chip on a technician card. */
export interface SkillChip {
  readonly label: string;
  /** `'ok'`, `'warn'` for an expiring certificate, `'bad'` for an expired one. */
  readonly tone: 'ok' | 'warn' | 'bad';
  /** Hover text — the full course name and its expiry, where there is one. */
  readonly title?: string;
  /** Stable key: a chip's label can repeat across the two sources. */
  readonly key: string;
}

/**
 * Demo line 2728. The rows behind a card's four source links — `FIG('jobs',
 * 'xreach', …)`, `FIG('timesheet', 'timesheets', …)`, `FIG('training', 'lms',
 * …)` and `FIG('CSS', 'css', …)`, each filtered to the technician.
 */
export interface TechnicianSources {
  /**
   * Every technician-app job, all statuses, flattened the way the demo's
   * `xreach` dataset is: `machine_id`, total `labour_h` and a `parts` list.
   */
  readonly jobs: readonly Record<string, unknown>[];
  readonly timesheets: TimesheetCtx['timesheets'];
  readonly css: readonly CustomerSatisfactionResponse[];
}

/** Demo lines 1897 and 2728. A card's source rows. */
export function technicianSources(ctx: TechniciansCtx, technician: string): TechnicianSources {
  return {
    jobs: ctx.techJobs
      .filter((j) => j.technician === technician)
      .map((j) => ({
        ...j,
        machine_id: j.machine,
        labour_h: j.labour_lines.reduce((a, l) => a + l.hours, 0),
        parts: j.parts_used.map((p) => p.part_no).join(' '),
      })),
    timesheets: ctx.timesheets.filter((t) => t.technician === technician),
    css: ctx.css.filter((r) => r.Technician === technician),
  };
}

/** Demo line 2721. One technician card. */
export interface TechnicianCard {
  readonly technician: string;
  readonly presence: TechnicianPresence;
  /** Initials for the avatar. */
  readonly initials: string;
  /** Job title from the directory, defaulted the demo's way. */
  readonly role: string;
  readonly onSite: boolean;
  /** Site display name, or `'Perth DC'` when the base is not one of the six. */
  readonly base: string;
  /** `'8/6 FIFO Perth · swing 09-03 → 09-11 · GPS −29.90, 121.00 …'`. */
  readonly rosterLine: string;
  /** `'Current job MM-48155 · 9 jobs completed · 2 open · W36 51.5 h · utilisation 74%'`. */
  readonly workLine: string;
  readonly stats: TechStats;
  readonly skills: readonly SkillChip[];
  /** Standing customer request, or `null`. */
  readonly preference: string | null;
  /** Grade band the demo prints off the roster's row order. */
  readonly grade: string | null;
  readonly sources: TechnicianSources;
}

/** Initials, first letter of each word — the demo's avatar. */
const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .join('');

/**
 * Demo line 2725. The grade string.
 *
 * The demo indexes the roster row: the first four technicians print `'FST-2'`
 * and the rest `'FST-3'`. Both branches of its ternary say `'FST-2'`, so the
 * effective rule is "row 0–3 → FST-2, else FST-3". Kept as written, and `null`
 * where the technician has no roster line at all.
 */
function gradeOf(roster: readonly RosterEntry[], entry: RosterEntry | undefined): string | null {
  if (!entry) return null;
  return roster.indexOf(entry) < 4 ? 'FST-2' : 'FST-3';
}

/** Demo line 2723. The roster line under the technician's name. */
function rosterLine(t: TechnicianPresence, entry: RosterEntry | undefined): string {
  const mine = MINES[t.base as MineCode];
  return [
    mine ? mine.name : 'Perth DC',
    entry?.Roster ?? '',
    entry ? `swing ${entry['Swing start'].slice(5)} → ${entry['Swing end'].slice(5)}` : '',
    `GPS ${t.lat.toFixed(2)}, ${t.lon.toFixed(2)}`,
    `last seen ${t.last_seen.slice(11)}`,
    t.vehicle,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Demo line 2724. The work line. */
function workLine(t: TechnicianPresence, stats: TechStats): string {
  return [
    t.current_job ? `Current job ${t.current_job}` : '',
    `${stats.done} jobs completed`,
    `${stats.open.length} open`,
    `W36 ${stats.hours} h`,
    `utilisation ${stats.util}%`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Demo line 2726. The chips: the presence record's own skills, then one chip
 * per certificate that is not current.
 *
 * A presence skill carrying the word "expiring" is already flagged in the
 * source string, which is why the demo tints it without consulting the LMS.
 */
function skillChips(t: TechnicianPresence, expiring: readonly TrainingRecord[]): SkillChip[] {
  const fromPresence: SkillChip[] = t.skills.map((s, i) => ({
    label: s,
    tone: /expiring/i.test(s) ? 'warn' : 'ok',
    key: `skill:${i}`,
  }));
  // The demo tints an `'Expired'` record red and an expiring one amber.
  // `TrainingStatus` is `'Current' | 'Expiring'` — the shipped pack records no
  // lapsed certificate — so only the amber branch can fire, and the red one is
  // dropped rather than left as dead code the type checker has to be argued
  // with. If a lapsed record ever ships, widen the schema union first.
  const fromLms: SkillChip[] = expiring.map((x) => ({
    label: `${x.course.split(' (')[0].slice(0, 26)} · ${x.status.toLowerCase()} ${x.expires.slice(5)}`,
    tone: 'warn',
    title: `${x.course} — ${x.status} ${x.expires}`,
    key: `lms:${x.lms_id}`,
  }));
  return [...fromPresence, ...fromLms];
}

/** Demo line 2721. One card per technician, in roster order. */
export function technicianCards(ctx: TechniciansCtx): TechnicianCard[] {
  return ctx.techs.map((t) => {
    const person = ctx.people.find((p) => p.name === t.technician);
    const entry = ctx.roster.find((r) => r.Technician === t.technician);
    const stats = techStats(t.technician, ctx);
    return {
      technician: t.technician,
      presence: t,
      initials: initialsOf(t.technician),
      role: person?.role ?? 'Field Service Technician',
      onSite: t.status === 'On site',
      base: MINES[t.base as MineCode] ? MINES[t.base as MineCode].name : 'Perth DC',
      rosterLine: rosterLine(t, entry),
      workLine: workLine(t, stats),
      stats,
      skills: skillChips(t, stats.expiring),
      preference: entry?.['Customer preference'] ?? null,
      grade: gradeOf(ctx.roster, entry),
      sources: technicianSources(ctx, t.technician),
    };
  });
}

/** Demo line 2728. The proposals table shows the first fourteen. */
export const DISPATCH_LIMIT = 14;

/** One row of the dispatch table, with the labels already written. */
export interface DispatchRow extends DispatchProposal {
  /** `'Kalbarri'` — the demo prints the first word of the site name. */
  readonly siteShort: string;
  /** Stable key: `ref` is a job id or a work-order id and is unique across both. */
  readonly key: string;
}

/** Demo line 2728. The agent's proposed assignments, first fourteen. */
export function dispatchRows(ctx: TechniciansCtx): DispatchRow[] {
  return dispatchProposals(ctx)
    .slice(0, DISPATCH_LIMIT)
    .map((p) => ({ ...p, siteShort: p.site.split(' ')[0], key: p.ref }));
}

/** Demo line 2729. The certificates table shows the first sixteen. */
export const CERTIFICATE_LIMIT = 16;

/** Demo line 2729. Every record that is not current, soonest expiry first. */
export function expiringCertificates(ctx: TrainingCtx): TrainingRecord[] {
  return ctx.training
    .filter((x) => x.status !== 'Current')
    .slice()
    .sort((a, b) => a.expires.localeCompare(b.expires));
}

/** Demo line 2720. The header chip and the two figures under the roster. */
export interface TechnicianSummary {
  readonly onSite: number;
  readonly total: number;
  /** Mean utilisation across the roster. */
  readonly meanUtilisation: number;
  readonly openJobs: number;
  readonly notCurrent: number;
  readonly trainingRecords: number;
}

/** Demo line 2720. The page's counts. */
export function technicianSummary(
  ctx: TechniciansCtx,
  cards: readonly TechnicianCard[],
): TechnicianSummary {
  return {
    onSite: ctx.techs.filter((t) => t.status === 'On site').length,
    total: ctx.techs.length,
    meanUtilisation: meanUtil(ctx),
    openJobs: cards.reduce((a, c) => a + c.stats.open.length, 0),
    notCurrent: ctx.training.filter((x) => x.status !== 'Current').length,
    trainingRecords: ctx.training.length,
  };
}

/**
 * Demo line 2718. `stars` — the rating as filled and hollow stars plus the
 * figure, or `null` when no survey has come back.
 */
export function starRating(value: number | null): { stars: string; label: string } | null {
  if (value == null) return null;
  const filled = Math.round(value);
  return {
    stars: '★'.repeat(filled) + '☆'.repeat(5 - filled),
    label: value.toFixed(1),
  };
}
